/**
 * Turning a batch's recurring weekly shape into dated class occurrences.
 *
 * There is no "scheduled class" record in this system. A batch stores the shape
 * of its week — `meetingDays` (0 = Sun … 6 = Sat), or failing that `batchType`,
 * or failing that the institute's working days — plus the window it runs for
 * (`startDate` … `endDate`) and the time of day it meets. Every calendar surface
 * has to project that onto real dates itself, so it is done ONCE, here, and the
 * month grid and the week grid both consume the result. They used to each carry
 * their own copy of the rule and they disagreed with one another.
 *
 * The projection covers whatever range is asked for. An earlier version walked a
 * fixed 60 days forward from `batch.startDate` instead, which meant a batch
 * silently vanished from the calendar two months in — while it was still running.
 */

export const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5];
const DAY_MS = 86400000;

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

/**
 * The weekdays a batch actually meets on, most specific source first.
 *
 * `meetingDays` is the batch's own answer and always wins when set. `batchType`
 * only distinguishes a weekend cohort from a weekday one, so it is a fallback,
 * not an override — a WEEKDAY batch meeting Tue/Thu is described by
 * `meetingDays`, and reading `batchType` first would put it on all five days.
 */
export function batchMeetingDays(batch, workingDays) {
  if (Array.isArray(batch?.meetingDays) && batch.meetingDays.length > 0) {
    return batch.meetingDays;
  }
  if (batch?.batchType === 'WEEKEND') return [0, 6];
  return Array.isArray(workingDays) && workingDays.length > 0 ? workingDays : DEFAULT_WORKING_DAYS;
}

/**
 * Which dates the institute has declared off (or forced on).
 *
 * `isWorkingDay: false` is a holiday — no class runs. `isWorkingDay: true` is an
 * override that reinstates a day the weekly pattern would otherwise skip, which
 * is how a make-up Sunday is expressed. An override on a date also beats a
 * holiday on the same date, matching the backend's expected-class-day engine.
 */
export function buildDayOverrides(events = []) {
  const off = new Set();
  const on = new Set();

  for (const evt of events) {
    if (!evt?.startDate) continue;
    const from = startOfDay(evt.startDate);
    const to = startOfDay(evt.endDate || evt.startDate);
    if (!isValidDate(from) || !isValidDate(to)) continue;

    for (let c = new Date(from); c <= to; c = new Date(c.getTime() + DAY_MS)) {
      const key = c.toDateString();
      if (evt.isWorkingDay === true) on.add(key);
      else if (evt.isWorkingDay === false) off.add(key);
    }
  }

  return { off, on };
}

/**
 * The last date a batch can still hold a class.
 *
 * `endDate` is the plan; `completedAt` is the day someone pressed "End Batch".
 * The earlier of the two wins, so a cohort wrapped up early stops drawing
 * classes on the day it actually stopped rather than the day it was meant to.
 */
function lastDateFor(batch) {
  const candidates = [batch?.endDate, batch?.completedAt]
    .filter(Boolean)
    .map(startOfDay)
    .filter(isValidDate);
  if (candidates.length === 0) return null;
  return new Date(Math.min(...candidates.map((d) => d.getTime())));
}

/**
 * One class of one batch on one date.
 *
 * `timed` is the distinction the week grid turns on: a batch with class times
 * configured becomes a positioned block in the hour grid, and one without —
 * which is a great many of them, since the batch form lets both fields be saved
 * empty — becomes an all-day chip. Dropping the untimed ones instead (the old
 * behaviour) is why a running batch could be visible in the month view and
 * absent from the week view on the very same day.
 */
function occurrence(batch, date) {
  const startTime = batch.startTime || '';
  const endTime = batch.endTime || '';
  return {
    _itemType: 'batch',
    key: `${batch._id}:${date.getTime()}`,
    _id: batch._id,
    batch,
    date,
    startTime,
    endTime,
    timed: Boolean(startTime && endTime),
  };
}

/**
 * Every class occurrence between two dates, indexed by `Date.toDateString()`.
 *
 * @param {object[]} batches
 * @param {Date} rangeStart  inclusive
 * @param {Date} rangeEnd    inclusive
 * @param {object} [opts]
 * @param {number[]} [opts.workingDays]  institute-wide fallback pattern
 * @param {object[]} [opts.events]       InstituteCalendarEvents (holidays/overrides)
 * @param {boolean}  [opts.skipHolidays] default true
 * @returns {Map<string, object[]>}
 */
export function batchOccurrencesInRange(batches = [], rangeStart, rangeEnd, opts = {}) {
  const { workingDays, events = [], skipHolidays = true } = opts;
  const map = new Map();

  const from = startOfDay(rangeStart);
  const to = startOfDay(rangeEnd);
  if (!isValidDate(from) || !isValidDate(to) || from > to) return map;

  const { off, on } = skipHolidays ? buildDayOverrides(events) : { off: new Set(), on: new Set() };

  for (const batch of batches) {
    if (!batch?.startDate) continue; // nothing to anchor the recurrence to
    const batchStart = startOfDay(batch.startDate);
    if (!isValidDate(batchStart)) continue;

    const batchEnd = lastDateFor(batch);
    const days = batchMeetingDays(batch, workingDays);

    const walkFrom = batchStart > from ? batchStart : from;
    const walkTo = batchEnd && batchEnd < to ? batchEnd : to;

    for (let c = new Date(walkFrom); c <= walkTo; c = new Date(c.getTime() + DAY_MS)) {
      const key = c.toDateString();
      // An explicit working-day override reinstates the class even when the
      // date falls outside the batch's usual weekly pattern.
      const overridden = on.has(key);
      if (!overridden && !days.includes(c.getDay())) continue;
      if (!overridden && off.has(key)) continue;

      if (!map.has(key)) map.set(key, []);
      map.get(key).push(occurrence(batch, new Date(c)));
    }
  }

  // Timed classes first and in clock order, so a day reads top-to-bottom.
  for (const list of map.values()) {
    list.sort((a, b) => {
      if (a.timed !== b.timed) return a.timed ? -1 : 1;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }

  return map;
}

/** The 12-hour label for an 'HH:mm' string. '' for anything unparseable. */
export function formatClassTime(value) {
  if (!value) return '';
  const [hh, mm] = String(value).split(':').map(Number);
  if (!Number.isFinite(hh)) return '';
  const suffix = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return mm ? `${h12}:${String(mm).padStart(2, '0')} ${suffix}` : `${h12} ${suffix}`;
}
